import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { listActualPatientVisitSettingsQuery } from '@shared/services/gql.service';
import {
  decimalAdd,
  decimalDifference,
  decimalDivide,
  decimalMultiply,
  decimalRoundingToNumber,
} from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { PatientVisitsValues } from '../types/investigator-forecast.types';

@Injectable({ providedIn: 'root' })
export class InvestigatorPatientVisits {
  editMode$ = new BehaviorSubject<boolean>(false);

  private precision = 2;

  getDiscontinuedDifference(
    averageCompletedPatientCost: number,
    remainingPatientsToDiscontinueCost: number
  ) {
    return decimalDifference(
      averageCompletedPatientCost,
      remainingPatientsToDiscontinueCost,
      this.precision
    );
  }

  getForecastedRemainingInvestigatorCosts(
    forecastedRemainingVisitCosts: number,
    forecastedRemainingInvoiceables: number
  ) {
    return decimalAdd(forecastedRemainingVisitCosts, forecastedRemainingInvoiceables);
  }

  getForecastedRemainingInvoiceables(
    invoiceablesPerc: number,
    forecastedRemainingVisitCosts: number
  ) {
    return decimalMultiply(invoiceablesPerc, forecastedRemainingVisitCosts, 4);
  }

  // eslint-disable-next-line max-lines-per-function
  getInitialSummaryValues(
    patientDataList: listActualPatientVisitSettingsQuery[],
    spendToDate: number
  ): PatientVisitsValues {
    const patientVisitData = patientDataList[0]; // last updated values

    const invoiceablesPerc = decimalDivide(patientVisitData.invoiceables_percent || 0, 100);

    const forecastedRemainingInvoiceables = this.getForecastedRemainingInvoiceables(
      invoiceablesPerc,
      patientVisitData.forecasted_remaining_visit_costs
    );

    const forecastedRemainingInvestigatorCosts = this.getForecastedRemainingInvestigatorCosts(
      patientVisitData.forecasted_remaining_visit_costs,
      forecastedRemainingInvoiceables
    );

    const invoiceablesDistribution = decimalDifference(100, patientVisitData.overhead_percent || 0);

    // discontinued
    const averageCompletedPatientCost = decimalRoundingToNumber(
      patientVisitData.average_completed_patient_cost,
      this.precision
    );

    const remainingPatientsToDiscontinuePerc = this.toPercent(
      Math.round(patientVisitData.remaining_patients_to_discontinue_percent || 0)
    );

    const remainingPatientsToDiscontinueCost = this.getRemainingPatientsToDiscontinueCost(
      decimalDivide(remainingPatientsToDiscontinuePerc, 100),
      averageCompletedPatientCost
    );

    const totalDiscontinuedPerc = decimalDivide(patientVisitData.discontinued_percent || 0, 100);

    const currentDiscontinuedPerc = decimalDivide(
      patientVisitData.current_discontinued_percent || 0,
      100
    );

    const currentEnrolledAmount = patientVisitData.current_enrolled_amount || 0;

    const patientRemainingToDiscontinueAmount = this.getPatientRemainingToDiscontinueAmount(
      totalDiscontinuedPerc,
      currentDiscontinuedPerc,
      currentEnrolledAmount
    );

    const patientRemainingToDiscontinueAmountLeft = this.getPatientRemainingToDiscontinueAmountLeft(
      currentEnrolledAmount,
      totalDiscontinuedPerc
    );

    const differentPatientCost = this.getDiscontinuedDifference(
      averageCompletedPatientCost,
      remainingPatientsToDiscontinueCost
    );

    const totalDiscontinued = this.getTotalDiscontinued(
      differentPatientCost,
      patientRemainingToDiscontinueAmount
    );

    // Spend to date and remaining section

    const totalForecastedRemainingInvestigatorCosts =
      this.getTotalForecastedRemainingInvestigatorCosts(
        forecastedRemainingInvestigatorCosts,
        totalDiscontinued
      );

    const totalForecastedInvestigatorAmountThroughEndOfTrial =
      this.getTotalForecastedInvestigatorAmountThroughEndOfTrial(
        totalForecastedRemainingInvestigatorCosts,
        spendToDate
      );

    return {
      averageCompletedPatientCost,
      avgPatientCost: averageCompletedPatientCost,
      currentDiscontinuedAmount: patientVisitData.current_discontinued_amount,
      currentDiscontinuedPerc,
      currentEnrolledAmount,
      currentInvoiceablesPercent: this.toPercent(
        decimalDivide(patientVisitData.current_invoiceables_percent, 100)
      ),

      differentPatientCost,
      forecastedRemainingInvestigatorCosts,
      forecastedRemainingInvoiceables,
      // Remaining Costs section
      forecastedRemainingVisitCosts: patientVisitData.forecasted_remaining_visit_costs,

      invoiceablesDistribution,
      invoiceablesPerc: this.toPercent(patientVisitData.invoiceables_percent || 0),

      invoiceablesToDate: patientVisitData.invoicables_to_date,
      patientRemainingToDiscontinueAmount,
      patientRemainingToDiscontinueAmountLeft,
      remainingAmount: decimalDifference(
        totalForecastedInvestigatorAmountThroughEndOfTrial,
        spendToDate
      ),
      remainingPatientsToDiscontinueCost,

      remainingPatientsToDiscontinuePerc,
      spendToDateAmount: spendToDate,
      totalDiscontinued,
      // Discontinued section
      totalDiscontinuedPerc: this.toPercent(patientVisitData.discontinued_percent || 0),
      totalForecastedInvestigatorAmountThroughEndOfTrial,

      totalForecastedRemainingInvestigatorCosts,
      // right section
      totalInvestigatorCosts: totalForecastedInvestigatorAmountThroughEndOfTrial,
      totalSpendToDate: spendToDate,
      // Spend to date and remaining section
      visitCostsToDate: patientVisitData.visit_costs_to_date,
    };
  }

  getPatientRemainingToDiscontinueAmount(
    totalDiscontinuedPerc: number,
    currentDiscontinuedPerc: number,
    currentEnrolledAmount: number
  ) {
    const remainingToDiscontinue = decimalMultiply(
      decimalDifference(totalDiscontinuedPerc, currentDiscontinuedPerc),
      currentEnrolledAmount,
      4
    );

    return remainingToDiscontinue >= 0 ? remainingToDiscontinue : 0;
  }

  getPatientRemainingToDiscontinueAmountLeft(
    currentEnrolledAmount: number,
    discontinuedPercent: number
  ) {
    return decimalMultiply(currentEnrolledAmount, discontinuedPercent, this.precision);
  }

  getRemainingPatientsToDiscontinueCost(
    remainingPatientsToDiscontinuePerc: number,
    averageCompletedPatientCost: number
  ) {
    return decimalMultiply(
      remainingPatientsToDiscontinuePerc,
      averageCompletedPatientCost,
      this.precision
    );
  }

  getTotalDiscontinued(discontinuedDifference: number, patientsRemainingToDiscontinue: number) {
    return decimalMultiply(
      discontinuedDifference,
      Utils.roundToNumber(patientsRemainingToDiscontinue, 2),
      4
    );
  }

  getTotalForecastedInvestigatorAmountThroughEndOfTrial(
    totalForecastedRemainingInvestigatorCosts: number,
    totalSpendToDate: number
  ) {
    return decimalAdd(totalForecastedRemainingInvestigatorCosts, totalSpendToDate, this.precision);
  }

  getTotalForecastedRemainingInvestigatorCosts(
    forecastedRemainingInvestigatorCosts: number,
    totalDiscontinued: number
  ) {
    return decimalDifference(
      forecastedRemainingInvestigatorCosts,
      totalDiscontinued,
      this.precision
    );
  }

  private toPercent(value: number): number {
    return decimalRoundingToNumber(value, this.precision);
  }
}
